import { cleanPhone } from '@/shared/lib/phone';
import type { GetDialogAudienceRequest } from './model/types';
import { ApiDialogActionType, ApiDialogAudienceType, ApiDialogOptionType } from '@/core/generated/api';
import type {
    DialogTemplateResponse,
    PublishDialogBroadcastRequest,
    SaveDialogTemplateRequest,
} from '@/core/generated/api';
import type { DialogBroadcastFormValues, DialogTemplateFormValues } from './model/schema';

/** Подготавливает поля редактора шаблона */
export const dialogTemplateToForm = (item?: DialogTemplateResponse): DialogTemplateFormValues => ({
    code: item?.code ?? '',
    title: item?.title ?? '',
    text: item?.text ?? '',
    action: item?.action ?? ApiDialogActionType.Acknowledge,
    textInput: item?.textInput
        ? {
            label: item.textInput.label ?? '',
            placeholder: item.textInput.placeholder ?? '',
            maxLength: item.textInput.maxLength ?? 1000,
        }
        : null,
    options: item?.options?.map(option => ({
        optionType: option.optionType ?? ApiDialogOptionType.Continue,
        text: option.text ?? '',
    })) ?? [{
        optionType: ApiDialogOptionType.Continue,
        text: 'Понятно'
    }],
    routeName: item?.routeName ?? '',
    routeEntityId: item?.routeEntityId ?? '',
    isEnabled: item?.isEnabled ?? true,
});

/** Преобразует поля редактора в команду сохранения */
export const dialogTemplateToRequest = (
    values: DialogTemplateFormValues,
    version?: string
): SaveDialogTemplateRequest => ({
    code: values.code,
    title: values.title.trim(),
    text: values.text.trim(),
    action: values.action,
    textInput: values.textInput
        ? {
            label: values.textInput.label.trim(),
            placeholder: values.textInput.placeholder.trim() || null,
            maxLength: values.textInput.maxLength,
        }
        : undefined,
    options: values.options.map(option => ({
        optionType: option.optionType,
        text: option.text.trim()
    })),
    routeName: values.routeName.trim() || null,
    routeEntityId: values.routeEntityId.trim() || null,
    isEnabled: values.isEnabled,
    version,
});

/** Подготавливает новый выпуск */
export const createDialogBroadcastDefaults = (): DialogBroadcastFormValues => ({
    templateId: '',
    name: '',
    audience: ApiDialogAudienceType.All,
    cityId: null,
    nameSearch: '',
    phoneSearch: '',
    petNameSearch: '',
    activeFrom: null,
    expiresAt: null,
});

/** Преобразует фильтры формы в запрос аудитории */
export const dialogAudienceToRequest = (
    values: DialogBroadcastFormValues,
    first = 0
): GetDialogAudienceRequest => ({
    audience: values.audience,
    cityId: values.cityId ?? undefined,
    nameSearch: values.nameSearch.trim() || undefined,
    phoneSearch: cleanPhone(values.phoneSearch) ?? undefined,
    petNameSearch: values.petNameSearch.trim() || undefined,
    skipCount: first,
    takeCount: 10,
});

/** Преобразует проверенную аудиторию в команду выпуска */
export const dialogBroadcastToRequest = (
    values: DialogBroadcastFormValues,
    requestId: string,
    allMatching: boolean,
    selected: string[],
): PublishDialogBroadcastRequest => ({
    ...dialogAudienceToRequest(values),
    requestId,
    templateId: values.templateId,
    name: values.name.trim(),
    activeFrom: values.activeFrom?.toISOString() ?? null,
    expiresAt: values.expiresAt?.toISOString() ?? null,
    allMatching,
    selectedAppUserIds: allMatching
        ? []
        : [...selected].sort(),
});

/** Создаёт пару кнопок выбранного режима диалога */
export const dialogActionToOptions = (
    action: ApiDialogActionType
): DialogTemplateFormValues['options'] => {
    if (action === ApiDialogActionType.CollectFeedback) return [
        {
            optionType: ApiDialogOptionType.Continue,
            text: 'Ответить',
        },
        {
            optionType: ApiDialogOptionType.Skip,
            text: 'Пропустить',
        },
    ];

    if (action === ApiDialogActionType.Acknowledge) return [{
        optionType: ApiDialogOptionType.Continue,
        text: 'Понятно',
    }];

    return [
        {
            optionType: ApiDialogOptionType.Yes,
            text: 'Да',
        },
        {
            optionType: ApiDialogOptionType.No,
            text: 'Нет',
        },
    ];
};
